// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { createColumnHelper, type ExpandedState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Table } from '@shared/ui-kit/components';
import type { GroupedLoansTableRow } from '@shared/advisory/aggregation/utils/build-loans-table-rows';

export interface LoansTableProps {
  readonly data: GroupedLoansTableRow[];
  readonly currency: string;
}

const columnHelper = createColumnHelper<GroupedLoansTableRow>();

export const useLoansTable = ({ currency }: LoansTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('bankName', {
        header: t('aggregator.loans.product', { ns: 'aggregator' }),
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2 truncate">
            {row.getCanExpand() ? (
              <>
                <Table.Expander expander={row} />
                <span>{row.original.bankName}</span>
              </>
            ) : (
              <span className="pl-6 text-blue-gray-muted">{row.original.name}</span>
            )}
          </div>
        ),
      }),

      columnHelper.accessor('grantedCapital', {
        header: t('aggregator.loans.granted-capital', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            value={info.getValue()}
            currency={currency}
            safeValue="-"
          />
        ),
      }),

      columnHelper.accessor('pendingAmount', {
        header: t('aggregator.loans.pending-amount', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            value={info.getValue()}
            currency={currency}
            safeValue="-"
          />
        ),
      }),

      columnHelper.accessor('interestRate', {
        header: t('aggregator.loans.interest-rate', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => {
          if (info.row.getCanExpand()) {
            return null;
          }

          return (
            <Table.Data
              format="percentage"
              value={info.getValue()}
              safeValue="-"
              validate
              applyPercentageTransform={false}
            />
          );
        },
      }),

      columnHelper.accessor('maturityDate', {
        header: t('common.expiration-date', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => {
          if (info.row.getCanExpand()) {
            return null;
          }

          return (
            <Table.Data
              format="date"
              value={info.getValue()}
              formatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }}
              safeValue="-"
            />
          );
        },
      }),

      columnHelper.accessor('monthlyPayment', {
        header: t('aggregator.loans.monthly-payment', { ns: 'aggregator' }),
        meta: { alignment: 'end' },
        cell: info => {
          const value = info.getValue();

          if (value === null) {
            return <span>-</span>;
          }

          return (
            <span className="money-display inline-flex items-baseline gap-x-0.5">
              <Table.Data
                format="currency"
                value={value}
                currency={currency}
                container={false}
              />
              <span className="text-blue-gray-muted">/m</span>
            </span>
          );
        },
      }),
    ],
    [currency, t],
  );

  const initialExpanded: ExpandedState = true;

  return {
    columns,
    initialExpanded,
  };
};
